import { useEffect, useRef, useState } from "react"
import { Smile } from "lucide-react"
import { CallEmojiPicker } from "./CallEmojiPicker"
import { REACTION_EMOJI, applyReactionSkinTone } from "./callSignals"
import type { ActiveReaction } from "./useReactions"

type ReactionsMenuProps = {
  onSelect: (emoji: string) => void
  onClose: () => void
  /** The attendee's default skin tone from User preferences. */
  skinTone?: string
}

/**
 * Quick reactions plus the complete searchable emoji collection. Picking one keeps the menu
 * open; Escape, the React button, or a click anywhere else closes it.
 */
export function ReactionsMenu({ onSelect, onClose, skinTone = "" }: ReactionsMenuProps) {
  const firstRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const [allEmojiOpen, setAllEmojiOpen] = useState(false)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const closeAndRestoreFocus = () => {
    closeRef.current()
    document.querySelector<HTMLButtonElement>("[data-testid=call-open-reactions]")?.focus()
  }
  useEffect(() => {
    firstRef.current?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeRef.current()
        document.querySelector<HTMLButtonElement>("[data-testid=call-open-reactions]")?.focus()
      }
    }
    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (!target || menuRef.current?.contains(target)) return
      if (target instanceof Element && target.closest("[data-testid=call-open-reactions]")) return
      closeRef.current()
    }
    document.addEventListener("keydown", handleKey)
    document.addEventListener("mousedown", handlePointer)
    document.addEventListener("touchstart", handlePointer)
    return () => {
      document.removeEventListener("keydown", handleKey)
      document.removeEventListener("mousedown", handlePointer)
      document.removeEventListener("touchstart", handlePointer)
    }
  }, [])

  return (
    <div
      ref={menuRef}
      role="dialog"
      aria-label="Send a reaction"
      data-testid="call-reactions-menu"
      className={`absolute bottom-24 left-1/2 z-[45] max-w-[calc(100%-1.5rem)] -translate-x-1/2 rounded-xl bg-[#0f1419] shadow-2xl ring-1 ring-white/10 ${allEmojiOpen ? "w-[352px]" : "w-max p-2"}`}
    >
      {allEmojiOpen ? (
        <CallEmojiPicker onSelect={onSelect} onClose={closeAndRestoreFocus} skinTone={skinTone} />
      ) : (
        <>
          <div role="menu" aria-label="Quick reactions" className="flex flex-wrap justify-center gap-1">
            {REACTION_EMOJI.map((base, index) => {
              const emoji = applyReactionSkinTone(base, skinTone)
              return (
                <button
                  key={base}
                  ref={index === 0 ? firstRef : undefined}
                  type="button"
                  role="menuitem"
                  aria-label={`React with ${emoji}`}
                  data-testid={`call-reaction-${index}`}
                  onClick={() => onSelect(emoji)}
                  className="flex h-10 w-10 items-center justify-center rounded-full text-2xl leading-none transition hover:scale-110 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
                >
                  {emoji}
                </button>
              )
            })}
          </div>
          <button type="button" onClick={() => setAllEmojiOpen(true)} className="mt-1 flex w-full items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white/80 hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f6a35c]">
            <Smile className="h-3.5 w-3.5" />
            All emoji
          </button>
        </>
      )}
    </div>
  )
}

const FLOAT_KEYFRAMES = `
@keyframes wf-reaction-float {
  0% { transform: translateY(0) scale(0.6); opacity: 0; }
  10% { transform: translateY(-10px) scale(1.1); opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateY(-220px) scale(1); opacity: 0; }
}`

/**
 * Floating reactions over the stage (like Prebuilt), plus a polite live region so screen
 * readers hear "Dana reacted with 👏".
 */
/** Horizontal lane (percent from the left) for a reaction, fixed by its id for its whole flight. */
export const reactionLane = (id: string) => {
  let hash = 0
  for (let index = 0; index < id.length; index += 1) hash = (hash * 31 + id.charCodeAt(index)) >>> 0
  return 12 + (hash % 60)
}

export function ReactionsOverlay({ reactions, announcement }: { reactions: ActiveReaction[]; announcement: string | null }) {
  return (
    <>
      <style>{FLOAT_KEYFRAMES}</style>
      <div data-testid="call-reactions-overlay" aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-24 top-0 z-[42] overflow-hidden">
        {reactions.map((reaction) => (
          <div
            key={reaction.id}
            data-testid="call-reaction-float"
            className="absolute bottom-0 flex flex-col items-center gap-1"
            style={{ left: `${reactionLane(reaction.id)}%`, animation: "wf-reaction-float 4s ease-out forwards" }}
          >
            <span className="text-4xl drop-shadow-lg">{reaction.emoji}</span>
            <span className="rounded-full bg-black/60 px-2 py-0.5 text-[11px] font-medium text-white">{reaction.local ? "You" : reaction.name}</span>
          </div>
        ))}
      </div>
      <div role="status" aria-live="polite" data-testid="call-reaction-announcement" className="sr-only">
        {announcement}
      </div>
    </>
  )
}
